import type {
	Annotation,
	AnnotationPatch,
	Boon,
	Chapter,
	Enemy,
	EntityType,
	GameTable,
	GearItem,
	GearItemPatch,
	GearSlot,
	Page,
	PageListItem,
	Skill,
} from "@shared/entities";
import { queryOptions } from "@tanstack/react-query";
import { api } from "./api";
import { queryClient } from "./query-client";

export const chaptersQuery = queryOptions({ queryKey: ["chapters"], queryFn: ({ signal }) => api.get<Chapter[]>("/game/chapters", signal) });
export const chapterQuery = (id: string) =>
	queryOptions({ queryKey: ["chapters", id], queryFn: ({ signal }) => api.get<Chapter>(`/game/chapters/${id}`, signal) });

export const enemiesQuery = queryOptions({ queryKey: ["enemies"], queryFn: ({ signal }) => api.get<Enemy[]>("/game/enemies", signal) });
export const enemyQuery = (id: string) =>
	queryOptions({ queryKey: ["enemies", id], queryFn: ({ signal }) => api.get<Enemy>(`/game/enemies/${id}`, signal) });

export const gearQuery = queryOptions({ queryKey: ["gear"], queryFn: ({ signal }) => api.get<GearSlot[]>("/game/gear", signal) });

export const skillsQuery = queryOptions({ queryKey: ["skills"], queryFn: ({ signal }) => api.get<Skill[]>("/game/skills", signal) });
export const skillQuery = (id: string) =>
	queryOptions({ queryKey: ["skills", id], queryFn: ({ signal }) => api.get<Skill>(`/game/skills/${id}`, signal) });
export const boonsQuery = queryOptions({ queryKey: ["boons"], queryFn: ({ signal }) => api.get<Boon[]>("/game/boons", signal) });

export const gameTableQuery = (name: string) =>
	queryOptions({ queryKey: ["game-tables", name], queryFn: ({ signal }) => api.get<GameTable>(`/game/tables/${name}`, signal), staleTime: 5 * 60_000 });

export const pagesQuery = queryOptions({ queryKey: ["pages"], queryFn: ({ signal }) => api.get<PageListItem[]>("/pages", signal) });
export const pageQuery = (slug: string) =>
	queryOptions({ queryKey: ["pages", slug], queryFn: ({ signal }) => api.get<Page>(`/pages/${slug}`, signal) });

/** Which list/detail caches an annotation write can change, per entity type. */
const keysFor: Record<EntityType, string[]> = {
	chapter: ["chapters"],
	enemy: ["enemies"],
	gear_slot: ["gear"],
	perk: ["gear"],
	skill: ["skills"],
	boon: ["boons"],
};

export async function saveAnnotation(type: EntityType, id: string, patch: AnnotationPatch): Promise<Annotation> {
	const saved = await api.patch<Annotation>(`/game/annotations/${type}/${id}`, patch);
	await Promise.all(keysFor[type].map((k) => queryClient.invalidateQueries({ queryKey: [k] })));
	return saved;
}

export const createPage = (body: Pick<Page, "slug" | "title" | "section" | "body">) => api.post<Page>("/pages", body);
export const updatePage = (id: string, body: Partial<Pick<Page, "slug" | "title" | "section" | "body" | "sort_order">>) =>
	api.patch<Page>(`/pages/${id}`, body);
export const deletePage = (id: string) => api.delete<null>(`/pages/${id}`);
export const invalidatePages = () => queryClient.invalidateQueries({ queryKey: ["pages"] });

// ── Gear catalog (codex-owned) ──────────────────────────────────────────────
export const gearItemsQuery = queryOptions({ queryKey: ["gear-items"], queryFn: ({ signal }) => api.get<GearItem[]>("/gear-items", signal) });
export const createGearItem = (body: Pick<GearItem, "slot" | "rarity" | "name"> & GearItemPatch) => api.post<GearItem>("/gear-items", body);
export const createGearItems = (body: { slot: GearItem["slot"]; rarity: GearItem["rarity"]; names: string[] }) => api.post<GearItem[]>("/gear-items/bulk", body);
export const updateGearItem = (id: string, body: GearItemPatch) => api.patch<GearItem>(`/gear-items/${id}`, body);
export const deleteGearItem = (id: string) => api.delete<null>(`/gear-items/${id}`);
export const invalidateGearItems = () => queryClient.invalidateQueries({ queryKey: ["gear-items"] });
